import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProduct } from '../services/productService';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext'; // ✅ import auth
import formatPrice from '../utils/formatPrice';
import "../css/productdetails.css";

const ProductDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate(); // ✅ for redirect
  const { addToCart } = useCart();
  const { user } = useAuth(); // ✅ get logged-in user

  const [product, setProduct] = useState(null);
  const [size, setSize] = useState('');
  const [color, setColor] = useState('');
  const [qty, setQty] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  // Touch swipe state
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    const distance = touchEndX.current - touchStartX.current;
    const isLeftSwipe = distance < -minSwipeDistance;
    const isRightSwipe = distance > minSwipeDistance;
    
    if (isLeftSwipe && product.images?.length > 1) {
      // Swipe left - go to next image
      setSelectedImageIndex((prev) => 
        prev < product.images.length - 1 ? prev + 1 : 0
      );
    } else if (isRightSwipe && product.images?.length > 1) {
      // Swipe right - go to previous image
      setSelectedImageIndex((prev) => 
        prev > 0 ? prev - 1 : product.images.length - 1
      );
    }
  };

  useEffect(() => {
    const load = async () => {
      const p = await getProduct(id);
      setProduct(p);
      setSize(p.sizes?.[0] || '');
      setColor(p.colors?.[0] || '');
    };
    load();
  }, [id]);

  if (!product) return null;

  // ✅ LOGIN PROTECTION ADDED HERE
  const handleAddToCart = async () => {
    if (!user) {
      alert('Login to purchase product');
      navigate('/auth'); // ✅ redirect to login
      return;
    }

    await addToCart({ productId: product._id, qty, size, color });
  };

  return (
    <div className="product-details-page">
      <div className="product-details-wrapper">
        <div className="product-gallery">
          <div 
            className={`main-image ${product.images?.length > 1 ? 'swipeable' : ''}`}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {product.images?.[selectedImageIndex] ? (
              <img src={product.images[selectedImageIndex]} alt={product.name} />
            ) : (
              <div className="product-image placeholder large">Image</div>
            )}
            {product.images?.length > 1 && (
              <div className="image-slider-container">
                <input
                  type="range"
                  min="0"
                  max={product.images.length - 1}
                  value={selectedImageIndex}
                  onChange={(e) => setSelectedImageIndex(Number(e.target.value))}
                  className="image-slider"
                />
                <div className="slider-indicators">
                  {product.images.map((_, index) => (
                    <span
                      key={index}
                      className={`slider-dot ${index === selectedImageIndex ? 'active' : ''}`}
                      onClick={() => setSelectedImageIndex(index)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="product-details">
          <h1>{product.name}</h1>

          <div className="price-row">
            <span className="price">{formatPrice(product.price)}</span>
            {product.mrp && (
              <span className="mrp">{formatPrice(product.mrp)}</span>
            )}
          </div>

          <p className="description">{product.description}</p>

          <div className="selectors">
            {product.sizes?.length > 0 && (
              <div className="selector">
                <label>Size</label>
                <div className="size-options">
                  {product.sizes.map((s) => (
                    <button
                      key={s}
                      className={s === size ? 'option active' : 'option'}
                      onClick={() => setSize(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

{product.colors?.length > 0 && (!product.tags?.includes('combos') || product.category?.slug !== 'combos') && (
              <div className="selector">
                <label>Color</label>
                <div className="color-options">
                  {product.colors.map((c) => (
                    <button
                      key={c}
                      className={c === color ? 'option active' : 'option'}
                      onClick={() => setColor(c)}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="selector">
              <label>Quantity</label>
              <input
                type="number"
                min="1"
                value={qty}
                onChange={(e) => setQty(Number(e.target.value))}
              />
            </div>
          </div>

          <button onClick={handleAddToCart} className="btn-add-to-cart">
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsPage;
